/**
 * Utilities for getting page level info
 *
 * @module
 */
import utilities from "@trinitymirrordigital/chameleon-utilities/dist/js/utilities.js";
import { log, err } from "./logging.js";

let pageUtilities = (function () {
  /**
   * Gets Unique Hit Id for the page request
   *
   * @function getUHID
   * @returns {String} The UHID, or undefined if not available
   */
  function getUHID() {
    var uhid = window.TMDATA.access("page.info.uhid");

    if (uhid) {
      log("Got uhid from Data Layer");
    } else {
      err("uhid not present in Data Layer");
    }

    return uhid;
  }

  /**
   * Gets the value of a specified query parameter from a URL
   *
   * e.g. getQueryParameter('token', 'http://www.mirror.co.uk/?token=abc')
   * will return the string 'abc'
   *
   * @function getQueryParameter
   * @param queryParam {string} - the query parameter to get
   * @param url {string} - url to get the query parameter from. If not passed then uses current page url
   * @returns {string} - the value of the query parameter, or null if not present in url
   */
  function getQueryParameter(queryParam, url) {
    var string = url ? url.substring(url.indexOf("?")) : window.location.search;

    var match = new RegExp("[?&]" + queryParam + "=([^&]*)").exec(string);
    return match && decodeURIComponent(match[1].replace(/\+/g, " "));
  }

  /**
   * Gets the domain of the page referrer
   *
   * @returns {string} - referrer domain or empty string if no referrer
   */
  function getReferrerDomain() {
    let domain = "";
    if (document.referrer) {
      let a = document.createElement("a");
      a.href = document.referrer;
      domain = a.hostname;
    }
    return domain;
  }

  /**
   * Gets users geographical region from cookie (UK or ROW)
   *
   * @returns {string} - region
   */
  function getGeoRegion() {
    let gsCookieValue = utilities.getCookie("GS_GROUP");

    if (gsCookieValue === "1") {
      return "UK";
    } else {
      return "ROW";
    }
  }

  /**
   * Note: This is a more reliable getCookie function.
   * The one in @trinitymirrordigital/chameleon-utilities/dist/js/utilities.js does not find the last cookie in the document.cookie string
   * and this version is not immediately updatable. The current \node_modules\@trinitymirrordigital\chameleon-utilities is to be left unchanged.
   * @param cname {string} - Name of the cookie string
   * @returns {string} - the value of the cookie
   */
  function getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].replace(/^ +/, "");
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  function deleteChildNodes(elem) {
    while (elem.firstChild) {
      elem.removeChild(elem.firstChild);
    }
    return elem;
  }

  function waitForElemToExist(selector, container = document.body) {
    return new Promise((resolve) => {
      const element = container.querySelector(selector);

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node === container.querySelector(selector)) {
              resolve(node);
              observer.disconnect();
            }
          });
        });
      });
      if (element) {
        resolve(element);
      } else {
        observer.observe(container, { childList: true, subtree: true });
      }
    });
  }

  return {
    deleteChildNodes,
    getGeoRegion,
    getUHID,
    getQueryParameter,
    getReferrerDomain,
    getCookie,
    waitForElemToExist,
  };
})();

export default pageUtilities;
